/**
 * chordChartExport.ts
 *
 * Converts a Sentisonics chord chart (array of ChordChartEntry) to an iRealPro
 * deep-link URL (irealb://) that can be opened directly in the iRealPro app.
 *
 * iRealPro format reference: https://github.com/infojunkie/ireal-musicxml
 *
 * The music string format uses a 16-cell grid per row.
 * Barlines: [ ] { } | Z
 * Chord tokens: root + iRealPro quality suffix + optional /bass
 * Non-standard chord suffixes are wrapped in *...* asterisks.
 * The music data is obfuscated with a self-inverse swap cipher before encoding.
 */

// ---------------------------------------------------------------------------
// Types (mirrored from App.tsx — import these from a shared types file once
// the project has one)
// ---------------------------------------------------------------------------

export type ChordSegment = {
  root_pc: number;
  third_quality: string;       // Display symbol: "m" | "°" | "+" | "" (major)
  third_quality_wbn: string;   // Same but used for Roman/Nashville display
  extension: string;           // Display extension: "∆7" | "7" | "9" | "♭9" | etc.
  extension_wbn: string;
  bass_pc: number;
  weight: number;
  mode: number;
  chord_qual: number;          // 0=Major 1=Minor 2=Dominant 3=Diminished 4=Augmented 5=AltDom
};

export type ChordChartEntry = {
  name: string;
  voicing: number[];
  isSeparator: boolean;
  segment?: ChordSegment;
};

export type IrealProExportOptions = {
  title?: string;
  composer?: string;
  style?: string;        // iRealPro display style name e.g. "Medium Swing", "Bossa Nova"
  key?: string;          // e.g. "C", "Bb", "A-" (minor keys use "-" suffix)
  bpm?: number;
  repeats?: number;      // default 3
  timeSignature?: '44' | '34' | '24' | '54' | '64' | '74' | '22' | '32' | '58' | '68' | '78' | '98' | '12' | null;
  // null = omit time signature entirely (valid in iRealPro)
  minorSymbol?: 'm' | '-' | '_';
  // Symbol used inside *...* wrapped custom suffixes for minor quality.
  // '-' is iRealPro's internal token; 'm' or '_' are display-friendly alternatives.
  // Default 'm'. Wire to chartSettings.minorSymbol when that setting is added.
  altSymbol?: 'sharp-flat' | 'plusminus' | 'explicit';
  // How to display ± (stacked #/b) in iRealPro export:
  // 'sharp-flat' → stacked display (not valid in iRealPro text, falls back to explicit)
  // 'plusminus'  → ± character (iRealPro may not render, but valid in *...*)
  // 'explicit'   → e.g. #9b9 (default — most readable in iRealPro)
  // Wire to chartSettings.altSymbol when that setting is added.
  openingBar?: 'doubleBar' | 'repeatStart';
  // The auto-displayed opening barline from the App UI (not in chordChart data).
  // 'doubleBar'   → plain section open '[T44...' (no extra barline on first measure)
  // 'repeatStart' → first measure gets iRealPro '{' repeat-start barline
  closingBar?: 'doubleBar' | 'repeatEnd';
  // The auto-displayed closing barline from the App UI (not in chordChart data).
  // 'doubleBar' → section ends with 'Z ' (default)
  // 'repeatEnd' → last measure gets iRealPro '}' barline before its content
};

// ---------------------------------------------------------------------------
// Note name helpers
// ---------------------------------------------------------------------------

/**
 * Returns the iRealPro-compatible note name for a pitch class (0–11),
 * spelled in the context of a given key (root_pc) and chord quality.
 *
 * Mirrors midiToName(pc, false, keyPc, chord_qual) from utils.ts,
 * outputting plain ASCII (#/b) rather than Unicode (♯/♭).
 *
 * pc:        the pitch class to name (0–11)
 * keyPc:     the root/key context for enharmonic spelling — use root_pc for
 *            both root and bass notes, matching App.tsx's assembleChordSymbol
 * chord_qual: 0=Major 1=Minor 2=Dominant 3=Diminished 4=Augmented 5=AltDom
 */
function pcToIrealNoteName(pc: number, keyPc: number, chord_qual: number): string {
  // Circle-of-fifths names: index = CoF position, [0]=sharp spelling, [1]=flat spelling
  const names: [string, string][] = [
    ['C', 'C'],
    ['G', 'G'],
    ['D', 'D'],
    ['A', 'A'],
    ['E', 'E'],
    ['B', 'B'],
    ['F#', 'Gb'],
    ['C#', 'Db'],
    ['G#', 'Ab'],
    ['D#', 'Eb'],
    ['A#', 'Bb'],
    ['F', 'F'],
  ];

  // Map chord_qual to the same mode value used by getSpellings():
  // major-like (0,2,4) → mode 0; minor-like (1,3,5) → mode 1
  const mode = chord_qual === 2 || chord_qual === 4 ? 0
    : chord_qual === 3 || chord_qual === 5 ? 1
      : chord_qual; // 0 or 1

  // Use keyPc (the root) as the spelling context, not pc (the note being named)
  // This matches midiToName(bass_pc, false, root_pc, chord_qual) in App.tsx
  const key = (keyPc * 7) % 12;
  const minor = 6 <= key && key <= 7 && mode === 1 ? 6 : 0;

  const noteNames: string[] = Array(12).fill('');
  for (let i = 0; i < 12; i++) {
    const useSharp = key <= ((chord_qual === 2 || chord_qual === 5) ? 6 : 5) + minor
      ? i <= 6 + minor + key - mode
      : !(i <= 7 + minor + key - mode);
    noteNames[(i * 7) % 12] = names[i][useSharp ? 0 : 1];
  }

  return noteNames[pc] || `pc${pc}`; // fallback should never trigger
}

// ---------------------------------------------------------------------------
// Chord quality mapping
// ---------------------------------------------------------------------------

// Suffixes iRealPro renders natively with proper formatting.
// Anything outside this set gets wrapped in *...* so iRealPro displays it as
// custom text rather than silently dropping or mangling the suffix.
// Suffixes containing parentheses are also always wrapped (parens have special
// meaning in iRealPro's format for alternate chord display).
//
// NOTE: iRealPro's protocol page lists newer qualities as 'min13', 'min^11',
// 'min^13', 'maj7#9', 'min7b6', 'min9b6'. Those are the chord picker's DISPLAY
// names, not URL tokens — iRealPro's parser accepts only sus/alt/add plus the
// characters [+ - ^ # b h o 0-9] in a quality, so 'm'/'i'/'n'/'j' end the match
// and the whole quality is dropped (F#min^13/F rendered as "F#/F").
// Use the '-' / '^' forms: iRealPro's own export of F#m△13/F is 'F#-^13/F'.
const IREAL_KNOWN_SUFFIXES = new Set([
  '', '5', '2', 'add9', 'add4', 'add2', 'sus', 'sus2', '6', '69',
  // NOTE: '-2' (minor add-2, which is what Harmona emits for Cm2) is NOT native —
  // verified against iRealPro, which does not recognise it. It falls through to
  // the *...* wrapper and displays as *m2*. Bare '-add2' IS native (verified).
  '^7', '^9', '^13', '^7#11', '^9#11', '^13#11', '^7#5', '^7#9',
  '7', '9', '11', '13',
  '7sus', '9sus', '13sus', '7b9sus', '7b13sus', '7susadd3',
  '7b9', '7#9', '7#11', '7b5', '7#5', '7b13', '7alt',
  '9#11', '9b5', '9#5',
  '13#11', '13b9', '13#9',
  '7#9#5', '7#9b5', '7#9#11', '7b9#11', '7b9b5', '7b9#5', '7b9#9', '7b9b13',
  '-', '-6', '-69', '-b6', '-#5', '-7', '-9', '-11', '-13', '-add2', '-add4',
  '-^7', '-^9', '-^11', '-^13', '-7b5', '-7b6', '-9b6',
  // Half-diminished. Harmona names these m7♭5 and m9♭5; iReal Pro has no m9♭5
  // in its vocabulary, so the minor branch maps extension '9♭5' to 'h9' (ø9),
  // which it does render. '-7b5' above is native, so m7♭5 is left alone.
  'o', 'o7', 'o^7', 'h7', 'h9',
  '+', '7#5', '^7#5', '9#5',
]);

/**
 * Maps Sentisonics third_quality + extension display strings to an iRealPro
 * chord modifier suffix string.
 *
 * Sentisonics display symbols (from Python backend get_cs.py):
 *   third_quality: "" (major), "m" (minor), "°" (dim), "+" (aug)
 *   extension uses Unicode: "∆" for major, "♯" for sharp, "♭" for flat, "±" for #/b stack
 *
 * Unknown suffixes are returned as-is — iRealPro handles them fine unless they
 * contain parentheses, which are handled by the caller.
 */
function toIrealModifiers(third_quality: string, extension: string): { suffix: string } {
  // Normalize Unicode accidentals to ASCII for lookup
  const ext = extension
    .replace(/∆/g, '△')
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b')
    .replace(/±/g, '#b');

  let suffix = '';

  // -------------------------------------------------------------------------
  // MAJOR quality (third_quality === "")
  // -------------------------------------------------------------------------
  if (third_quality === '') {
    if (ext === '') suffix = '';
    else if (ext === '5') suffix = '5';
    else if (ext === '2' || ext === 'add2') suffix = '2';
    else if (ext === 'add9') suffix = 'add9';
    else if (ext === 'add4') suffix = 'add4';
    else if (ext === 'sus' || ext === 'sus4') suffix = 'sus';
    else if (ext === 'sus2') suffix = 'sus2';
    else if (ext === '6') suffix = '6';
    else if (ext === '6/9' || ext === '69') suffix = '69';
    else if (ext === '△7') suffix = '^7';
    else if (ext === '△9') suffix = '^9';
    else if (ext === '△13') suffix = '^13';
    else if (ext === '△7#11') suffix = '^7#11';
    else if (ext === '△9#11') suffix = '^9#11';
    else if (ext === '△13#11') suffix = '^13#11';
    else if (ext === '△7#5') suffix = '^7#5';
    else if (ext === '△7#9') suffix = '^7#9';
    else if (ext === '7') suffix = '7';
    else if (ext === '9') suffix = '9';
    else if (ext === '11') suffix = '11';
    else if (ext === '13') suffix = '13';
    else if (ext === '7sus' || ext === '7sus4') suffix = '7sus';
    else if (ext === '9sus') suffix = '9sus';
    else if (ext === '13sus') suffix = '13sus';
    else if (ext === '7b9sus') suffix = '7b9sus';
    else if (ext === '7b9') suffix = '7b9';
    else if (ext === '7#9') suffix = '7#9';
    else if (ext === '7#11') suffix = '7#11';
    else if (ext === '7b5') suffix = '7b5';
    else if (ext === '7#5') suffix = '7#5';
    else if (ext === '7b13') suffix = '7b13';
    else if (ext === '7b13sus') suffix = '7b13sus';
    else if (ext === '9#11') suffix = '9#11';
    else if (ext === '9b5') suffix = '9b5';
    else if (ext === '9#5') suffix = '9#5';
    else if (ext === '13#11') suffix = '13#11';
    else if (ext === '13b9') suffix = '13b9';
    else if (ext === '13#9') suffix = '13#9';
    else if (ext === '7alt' || ext === 'alt') suffix = '7alt';
    else if (ext === '7#9#5') suffix = '7#9#5';
    else if (ext === '7#9b5') suffix = '7#9b5';
    else if (ext === '7#9#11') suffix = '7#9#11';
    else if (ext === '7b9#11') suffix = '7b9#11';
    else if (ext === '7b9b5') suffix = '7b9b5';
    else if (ext === '7b9#5') suffix = '7b9#5';
    else if (ext === '7#b9' || ext === '7b9#9') suffix = '7b9#9';
    else if (ext === '7b9b13') suffix = '7b9b13';
    else suffix = ext; // unknown — will be wrapped below
  }

  // -------------------------------------------------------------------------
  // MINOR quality (third_quality === "m")
  // -------------------------------------------------------------------------
  else if (third_quality === 'm') {
    if (ext === '') suffix = '-';
    else if (ext === 'add2') suffix = '-add2';  // iRealPro native: renders as Cm2 superscript
    else if (ext === '2') suffix = '-2';        // not native → will be wrapped as *m2*
    // Harmona names this chord Cm2 (extension "2"), never "add9", so this branch
    // is unreachable in practice — kept only as a guard if the backend changes.
    else if (ext === 'add9') suffix = '-add9';
    // iReal Pro renders '-add4' natively (verified, like '-add2'), so keep the
    // added 4th rather than dropping it down to a bare '-'.
    else if (ext === 'add4') suffix = '-add4';
    else if (ext === '6') suffix = '-6';
    else if (ext === '6/9' || ext === '69') suffix = '-69';
    else if (ext === 'b6') suffix = '-b6';
    else if (ext === '#5') suffix = '-#5';
    else if (ext === '7') suffix = '-7';
    else if (ext === '9') suffix = '-9';
    else if (ext === '11') suffix = '-11';
    else if (ext === '13') suffix = '-13';
    else if (ext === '△7') suffix = '-^7';
    else if (ext === '△9') suffix = '-^9';
    else if (ext === '△11') suffix = '-^11';
    else if (ext === '△13') suffix = '-^13';
    else if (ext === '7b5') suffix = '-7b5';
    else if (ext === '9b5') suffix = 'h9';
    else if (ext === '7b13') suffix = '-7b6';
    else if (ext === '9b13') suffix = '-9b6';
    else suffix = `-${ext}`; // best-effort minor prefix
  }

  // -------------------------------------------------------------------------
  // DIMINISHED quality (third_quality === "°")
  // -------------------------------------------------------------------------
  else if (third_quality === '°') {
    if (ext === '') suffix = 'o';
    else if (ext === '7') suffix = 'o7';
    // Diminished triad with a major 7th. Harmona writes C°△7 and iReal Pro
    // renders 'o^7'. (Its dim-7 cousin, C°7(△7), has no iReal Pro spelling and
    // stays wrapped — parenthesised suffixes never can be native.)
    else if (ext === '△7') suffix = 'o^7';
    else if (ext === 'n7' || ext === '♮7') suffix = 'h7';
    else suffix = `o${ext}`;
  }

  // -------------------------------------------------------------------------
  // HALF-DIMINISHED
  // -------------------------------------------------------------------------
  else if (third_quality === 'ø' || third_quality === 'h') {
    if (ext === '' || ext === '7') suffix = 'h7';
    else if (ext === '9') suffix = 'h9';
    else suffix = `h${ext}`;
  }

  // -------------------------------------------------------------------------
  // AUGMENTED quality (third_quality === "+")
  // -------------------------------------------------------------------------
  else if (third_quality === '+') {
    if (ext === '') suffix = '+';
    else if (ext === '7') suffix = '7#5';
    else if (ext === '△7') suffix = '^7#5';
    else if (ext === '9') suffix = '9#5';
    else suffix = `+${ext}`;
  }

  // Fallback
  else {
    suffix = `${third_quality}${ext}`;
  }

  return { suffix };
}

// ---------------------------------------------------------------------------
// iRealPro obfuscation cipher
// ---------------------------------------------------------------------------

/**
 * Obfuscates/deobfuscates a 50-char block by swapping character positions.
 * This function is its own inverse (applying it twice returns the original).
 * Source: https://github.com/infojunkie/ireal-musicxml/blob/main/src/lib/parser.js
 */
function obfusc50(s: string): string {
  const chars = s.split('');
  for (let i = 0; i < 5; i++) {
    [chars[i], chars[49 - i]] = [chars[49 - i], chars[i]];
  }
  for (let i = 10; i < 24; i++) {
    [chars[i], chars[49 - i]] = [chars[49 - i], chars[i]];
  }
  return chars.join('');
}

/**
 * Scrambles a music string into iRealPro's obfuscated format.
 *
 * Encoding steps:
 *   1. Apply token substitutions: '| x'→'Kcl', '   '→'XyQ', ' |'→'LZ'
 *   2. Apply obfusc50 to each 50-char block while MORE than 51 chars remain;
 *      stop when ≤ 51 chars are left (those become the unscrambled tail).
 *
 * iRealPro's decoder mirrors this: it descrambles blocks while > 51 chars
 * remain, then applies reverse-substitutions (XyQ→'   ', LZ→' |', Kcl→'| x')
 * to the entire result including the unscrambled tail.
 *
 * The "> 51" boundary means the final 50-char block (when present) is always
 * left unscrambled — confirmed by diffing iRealPro's own exported URLs and by
 * two independent open-source iRealPro parsers that use the identical condition:
 *   https://github.com/ironss/accompaniser/blob/master/irealb_parser.lua
 *   https://github.com/infojunkie/ireal-musicxml/blob/main/src/lib/parser.js
 */
function scramble(s: string): string {
  // Substitution order matters: triple-spaces must be replaced before ' |'
  // so that 'C   |' → 'CXyQ|' (not 'C  LZ' if ' |' fires first).
  s = s.replace(/\| x/g, 'Kcl')
    .replace(/   /g, 'XyQ')
    .replace(/ \|/g, 'LZ');

  let result = '';
  while (s.length > 51) {
    result += obfusc50(s.substring(0, 50));
    s = s.substring(50);
  }
  return result + s;
}

// ---------------------------------------------------------------------------
// Music string builder
// ---------------------------------------------------------------------------

/**
 * Separator name constants — must match SEPARATORS in App.tsx exactly.
 */
const SEP = {
  barline: '|',
  doubleBarline: '𝄁',
  repeatStart: '𝄆',
  repeatEnd: '𝄇',
  repeatMeasure: '𝄎',
  doubleRepeatMeasure: '𝄏',
  slash: '/',
};

// Separators that act as measure boundaries (barlines).
const BARLINE_NAMES = new Set([
  SEP.barline, SEP.doubleBarline, SEP.repeatStart, SEP.repeatEnd,
]);

/** Returns true if an entry is a barline (measure boundary). */
function isBarline(entry: ChordChartEntry): boolean {
  return entry.isSeparator && BARLINE_NAMES.has(entry.name);
}

/** Returns true if an entry is a whole-measure repeat token. */
function isRepeatMeasure(entry: ChordChartEntry): boolean {
  return entry.isSeparator &&
    (entry.name === SEP.repeatMeasure || entry.name === SEP.doubleRepeatMeasure);
}

/** Returns true if an entry is a slash beat (repeat-previous-chord slot). */
function isSlash(entry: ChordChartEntry): boolean {
  return entry.isSeparator && entry.name === SEP.slash;
}

// ---------------------------------------------------------------------------
// Chord token builder (for a single chord entry)
// ---------------------------------------------------------------------------

/**
 * Returns the fully-expanded iRealPro suffix for a segment — the exact string
 * that gets tested against IREAL_KNOWN_SUFFIXES and emitted (bare or wrapped).
 *
 * Exported so the respelling resolver (chordRespell.ts) tests candidate
 * spellings against the same string the exporter will actually emit. Keep this
 * as the single place ± expansion happens.
 */
export function irealSuffixForSegment(
  segment: ChordSegment,
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit' = 'explicit',
): string {
  const { suffix } = toIrealModifiers(segment.third_quality, segment.extension);

  // ±-expansion: toIrealModifiers normalises ± → #b.
  // Expand #b based on altSymbol: 'explicit' → #<n>b<n>, 'plusminus' → ±<n>.
  // pre = digits before #b (e.g. '7' in '7#b5'), post = digits after (e.g. '5').
  // Keep pre as a leading number if it differs from post ('7#b5' → '7±5' / '7#5b5').
  if (altSymbol === 'plusminus') {
    return suffix.replace(/(\d*)#b(\d*)/g, (_m, pre, post) => {
      const n = post || pre;
      const prefix = pre && pre !== post ? pre : '';
      return prefix ? `${prefix}±${n}` : (n ? `±${n}` : '±');
    });
  }
  // 'explicit' and 'sharp-flat' (stacked glyph not valid in iRealPro text)
  return suffix.replace(/(\d*)#b(\d*)/g, (_m, pre, post) => {
    const n = post || pre;
    const prefix = pre && pre !== post ? pre : '';
    return prefix ? `${prefix}#${n}b${n}` : (n ? `#${n}b${n}` : '#b');
  });
}

/**
 * True when iRealPro renders this suffix natively (proper chord formatting).
 * False means the exporter will wrap it in *...* as custom text.
 * Parenthesised suffixes are never native — parens mean "alternate chord".
 */
export function isIrealNativeSuffix(expandedSuffix: string): boolean {
  return IREAL_KNOWN_SUFFIXES.has(expandedSuffix) && !/[()]/.test(expandedSuffix);
}

/**
 * Returns the iRealPro root (and bass) note name for a segment.
 * Exported so respelling suggestions can be shown to the user as real chord
 * symbols rather than bare suffixes.
 */
export function irealRootNameForSegment(segment: ChordSegment): string {
  return pcToIrealNoteName(segment.root_pc, segment.root_pc, segment.chord_qual);
}

/**
 * Returns the iRealPro chord string for one chord ChordChartEntry.
 * Never returns null — assumes caller has verified entry is not a separator.
 */
function chordEntryToToken(
  entry: ChordChartEntry,
  minorSymbol: 'm' | '-' | '_',
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit',
): string {
  if (!entry.segment) return 'N';   // N.C. fallback

  const { root_pc, bass_pc, chord_qual } = entry.segment;

  const rootName = pcToIrealNoteName(root_pc, root_pc, chord_qual);
  const expandedSuffix = irealSuffixForSegment(entry.segment, altSymbol);

  // Wrap in *...* when suffix is non-standard or contains parens.
  const needsWrap = !isIrealNativeSuffix(expandedSuffix);

  let modifierToken: string;
  if (needsWrap) {
    const displaySuffix = expandedSuffix
      .replace(/^-/, minorSymbol)
      .replace(/-\^/g, `${minorSymbol}^`)
      .replace(/△/g, '^');
    modifierToken = `*${displaySuffix}*`;
  } else {
    modifierToken = expandedSuffix;
  }

  const bassToken = bass_pc !== root_pc
    ? `/${pcToIrealNoteName(bass_pc, root_pc, chord_qual)}`
    : '';

  return `${rootName}${modifierToken}${bassToken}`;
}

// ---------------------------------------------------------------------------
// Measure segmentation
// ---------------------------------------------------------------------------

type MeasureSlot = {
  token: string;   // pre-built iRealPro chord token or 'p' for slash beat
};

type SourceMeasure = {
  /** iRealPro token for the barline that opens this measure, e.g. '|', '{', '}' */
  openBarToken: string | null;
  slots: MeasureSlot[];
  /** true if the measure is a whole-bar repeat (x or r token) */
  isRepeat: boolean;
  repeatToken: string;  // 'x' or 'r' if isRepeat
};

/**
 * Splits the flat chart array into SourceMeasures.
 * Barlines are stored as the opening token of the next measure (not the closer of the current).
 * The very first entry may or may not be a barline.
 */
function segmentIntoMeasures(
  chart: ChordChartEntry[],
  minorSymbol: 'm' | '-' | '_',
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit',
): SourceMeasure[] {
  const measures: SourceMeasure[] = [];
  let currentOpen: string | null = null;
  let currentSlots: MeasureSlot[] = [];

  const flush = (closeAsOpen: string | null) => {
    if (currentSlots.length > 0) {
      measures.push({ openBarToken: currentOpen, slots: currentSlots, isRepeat: false, repeatToken: '' });
      currentSlots = [];
    }
    currentOpen = closeAsOpen;
  };

  for (const entry of chart) {
    if (isRepeatMeasure(entry)) {
      flush(null);
      const tok = entry.name === SEP.repeatMeasure ? 'x' : 'r';
      measures.push({ openBarToken: currentOpen, slots: [], isRepeat: true, repeatToken: tok });
      currentOpen = null;
      continue;
    }

    if (isBarline(entry)) {
      const tok = barlineEntryToToken(entry);
      flush(tok);
      continue;
    }

    if (isSlash(entry)) {
      currentSlots.push({ token: 'p' });
      continue;
    }

    // Blank chord (em quad space \u2001) — occupies 1 cell as a silent blank space.
    // A space character in iRealPro advances position by 1 beat with no visible text.
    if (!entry.isSeparator && entry.name === '\u2001') {
      currentSlots.push({ token: ' ' });
      continue;
    }

    if (!entry.isSeparator && entry.segment) {
      currentSlots.push({ token: chordEntryToToken(entry, minorSymbol, altSymbol) });
      continue;
    }
    // anything else is skipped
  }

  // Flush any trailing measure (no final barline in chart)
  if (currentSlots.length > 0) {
    measures.push({ openBarToken: currentOpen, slots: currentSlots, isRepeat: false, repeatToken: '' });
  }

  return measures;
}

function barlineEntryToToken(entry: ChordChartEntry): string {
  switch (entry.name) {
    case SEP.barline: return '|';
    case SEP.doubleBarline: return 'Z';
    // iRealPro encodes the outermost section boundary with '[' (open) and 'Z' (close)
    // in buildMusicString. Repeat-start and repeat-end barlines inside the chart are
    // treated as plain barlines — the section wrapper handles the repeat.
    case SEP.repeatStart: return '|';
    case SEP.repeatEnd: return '|';
    default: return '|';
  }
}

// ---------------------------------------------------------------------------
// Cell-spacing logic
// ---------------------------------------------------------------------------

/**
 * Returns the number of beats (cells) per bar for a given time signature string.
 * We use the numerator as the cell count (iRealPro's model).
 */
function beatsPerBar(timeSignature: string | null | undefined): number {
  if (!timeSignature) return 4;
  const num = parseInt(timeSignature.slice(0, -1), 10);
  return isNaN(num) ? 4 : num;
}

/**
 * The widest chord symbol that still reads at full size when the cell next to
 * it is occupied — one limit per kind of obstacle, because they are not the
 * same size.
 *
 * A chord in the next cell is the harshest neighbour: its own symbol is drawn
 * there, so only a bare root (2) survives beside one. A slash is a single
 * diagonal stroke and the closing barline a thin rule, so both leave a little
 * more room — enough for a 3, which is why 'Cm' reads full size before a slash
 * but not before another chord.
 *
 * Widths are in superscript-glyph units (see symbolWidth), NOT characters.
 * Calibrated on charts checked in iRealPro:
 *   before a slash:   'A/C#' (2), 'Cm' and 'C2/E' (3) read fine;
 *                     'Cm7', 'A^7' and 'Ab^7' (4), 'Eb^13' (4.5) and
 *                     'D7b9sus/C' (7.5) do not
 *   before a chord:   'D' and 'A/C#' (2) read fine; 'Bm', 'Cm' and 'C2/E' (3)
 *                     do not, nor does 'Ab^7#11' (6)
 *   before a barline: 'Fm13/Bb' (4.5) does not — which is what puts the
 *                     chord/barline limit below 4.5
 *
 * A blank cell between the chord and the obstacle is unlimited room for a NATIVE
 * symbol — verified in iRealPro with the longest native symbols Harmona
 * produces, which all read at full size with one spacer after them.
 *
 * Custom *...* text is the exception. iRealPro draws it as plain text rather
 * than with its own chord glyphs, so it runs wider than a native symbol of the
 * same width and a single spacer does not always cover it. A wrapped symbol
 * gains only WIDTH_PER_BLANK_CELL per spacer instead of being exempted.
 *
 * These three numbers are the whole font-size policy — move them if symbols
 * shrink too eagerly or not eagerly enough.
 */
const WIDTH_BEFORE_SLASH = 3;
const WIDTH_BEFORE_CHORD = 2;
const WIDTH_PER_BLANK_CELL = 2;

/**
 * Estimated drawn width of a chord symbol, in superscript-glyph units.
 *
 * iRealPro draws a chord as a full-size root letter followed by its quality and
 * extension in superscript, with accidentals stacked above and the /bass tail
 * set below and to the right. Two consequences, both of which a character count
 * gets wrong:
 *
 *   - every glyph after the root is the same width, letter or digit alike, so
 *     'Cm' and 'C2' measure the same, and so do 'Cm7' and 'C^7';
 *   - the root's own accidental and the bass note add nothing at all, so 'A',
 *     'Ab' and 'Ab/Db' all measure the same;
 *   - an accidental ALTERING an extension is different: it sits inline in the
 *     superscript run rather than above the root, so '7b9' is wider than '79';
 *   - a two-digit extension number is only half a unit wider than a one-digit
 *     one — 'b13' over 'b9', '#11' over '#5' — not twice as wide.
 *
 *   root letter ....................................... ROOT_WIDTH (2)
 *   each quality glyph and extension number ........... 1
 *   a second digit in that number ..................... EXTRA_DIGIT_WIDTH (0.5)
 *   an accidental altering an extension ............... ACCIDENTAL_WIDTH (0.5)
 *   the root's own accidental — stacked above it ...... 0
 *   the /bass tail — set below and to the right ....... 0
 *   '*', the custom-text wrapper — markup, never drawn  0
 *
 * Exported so the width table can be asserted directly in the tests.
 *
 * The unit is one superscript glyph. Accidentals still break a digit run ('7#11' is '7' and '11', two
 * numbers), which is why they are skipped rather than stripped up front.
 */
const ROOT_WIDTH = 2;
const EXTRA_DIGIT_WIDTH = 0.5;
const ACCIDENTAL_WIDTH = 0.5;

export function symbolWidth(token: string): number {
  const bass = token.match(/\/[A-G][#b]?$/);
  const head = (bass ? token.slice(0, -bass[0].length) : token).replace(/\*/g, '');
  if (head.length === 0) return 0;

  // head[0] is the root letter; every glyph after it is superscript.
  let width = ROOT_WIDTH;
  let i = 1;

  // The root's own accidental is stacked above the letter and adds nothing.
  // ('b' is only ever a flat here — root letters are uppercase and iRealPro
  // writes minor as '-' — so this cannot swallow a quality glyph.)
  if (i < head.length && (head[i] === '#' || head[i] === 'b')) i += 1;

  while (i < head.length) {
    const ch = head[i];
    // Any later accidental alters an extension, and is drawn inline before it.
    if (ch === '#' || ch === 'b') { width += ACCIDENTAL_WIDTH; i += 1; continue; }

    if (ch >= '0' && ch <= '9') {
      let digits = 0;
      while (i < head.length && head[i] >= '0' && head[i] <= '9') { digits += 1; i += 1; }
      width += 1 + EXTRA_DIGIT_WIDTH * (digits - 1);
      continue;
    }

    width += 1;
    i += 1;
  }
  return width;
}

/**
 * One cell-occupying token, with the blank cells that follow it and whether its
 * symbol has to shrink.
 */
type Slot = { token: string; trailing: number; small: boolean };

/**
 * Lays out one measure's tokens across `cells` cells: how many blank cells each
 * token carries after it. Three shapes, all of which conserve the cell count.
 *
 * The count is the only thing the 16-cell row logic cares about, which is why
 * font size and commas are decided separately, afterwards.
 */
function allocateSlots(tokens: string[], cells: number): Slot[] {
  const n = tokens.length;
  const spare = cells - n;
  const slots: Slot[] = tokens.map(token => ({ token, trailing: 0, small: false }));

  const slashCount = tokens.filter(t => t === 'p').length;
  if (slashCount > 0) {
    // Slash bars: a slash pins its own beat, so it never takes spare cells, and
    // neither does the chord immediately before it — spare goes to the "free"
    // chords, LAST first, so the leftover beat lands before the barline rather
    // than wedged between two chords mid-measure.
    //
    //   | / / A7 |   → pp + A7(+1)          | / A7 / |  → p + A7 + p(+1)
    //   | / A7 |     → p + A7(+2)           | / / |     → pp(+2 on the last)
    const freeChords = tokens.reduce((count, t, i) =>
      t !== 'p' && tokens[i + 1] !== 'p' ? count + 1 : count, 0);
    const base = freeChords > 0 ? Math.floor(spare / freeChords) : 0;
    const extra = freeChords > 0 ? spare % freeChords : 0;

    let freeIdx = 0;
    for (let i = 0; i < n; i++) {
      const isLast = i === n - 1;
      if (tokens[i] === 'p') {
        // The final slash absorbs spare nothing else can take (| / A7 / |).
        if (isLast && freeChords === 0) slots[i].trailing = spare;
        continue;
      }
      if (tokens[i + 1] === 'p') continue;   // pinned by the slash after it
      slots[i].trailing = base + (freeIdx++ >= freeChords - extra ? 1 : 0);
    }
    return slots;
  }

  const spaceCount = tokens.filter(t => t === ' ').length;
  if (spaceCount > 0 && spare < n) {
    // Not enough spare for every token, but the bar has blank beats: let the
    // blanks absorb it so consecutive chords stay on consecutive beats.
    const basePerBlank = Math.floor(spare / spaceCount);
    const extraBlanks = spare % spaceCount;
    let blankSeen = 0;
    for (const slot of slots) {
      if (slot.token !== ' ') continue;
      slot.trailing = basePerBlank + (blankSeen < extraBlanks ? 1 : 0);
      blankSeen += 1;
    }
    return slots;
  }

  // Plain bar: spread the spare cells evenly, front-heavy.
  const base = Math.floor(spare / n);
  const extra = spare % n;
  for (let i = 0; i < n; i++) slots[i].trailing = base + (i < extra ? 1 : 0);
  return slots;
}

/**
 * Marks the chords that have to be drawn small: those whose symbol is wider
 * than the room between them and the next thing drawn.
 *
 * This runs for EVERY bar, not just slash bars. iRealPro does no automatic
 * formatting of its own — every 's' in a chart is one somebody put there — so
 * there is no house rule to defer to, and "the bar is full, shrink all of it"
 * would shrink chords that fit perfectly well.
 */
function assignFontSizes(slots: Slot[]): void {
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (slot.token === 'p' || slot.token === ' ') continue;

    // Walk past every blank cell to find what is actually drawn next — a slash,
    // another chord, or (running off the end of the bar) the closing barline.
    let blanks = slot.trailing;
    let j = i + 1;
    while (j < slots.length && slots[j].token === ' ') {
      blanks += 1 + slots[j].trailing;
      j += 1;
    }

    // A spacer is room enough for any native symbol; custom *...* text is drawn
    // wider, so it only gains WIDTH_PER_BLANK_CELL per spacer.
    const wrapped = slot.token.indexOf('*') !== -1;
    if (blanks > 0 && !wrapped) continue;

    const obstacle = j < slots.length ? slots[j].token : '|';
    const allowance = (obstacle === 'p' ? WIDTH_BEFORE_SLASH : WIDTH_BEFORE_CHORD) +
      (wrapped ? WIDTH_PER_BLANK_CELL * blanks : 0);
    slot.small = symbolWidth(slot.token) > allowance;
  }
}

/**
 * Writes the measure out: cell content, ',' delimiters and 's'/'l' font markers.
 *
 * The ',' is a PARSER requirement, not a font marker. Two chord tokens with
 * nothing between them are read by iRealPro as ONE chord: it takes the root,
 * runs its quality charset into the second chord's letter name, finds the
 * quality unparseable, drops it and resumes at /bass, so 'Ab^7#11F-13/Bb'
 * renders as "Ab/Bb". Any chord followed directly by another gets a ',' whatever
 * size it is in.
 *
 * 's' and 'l' are idempotent in iRealPro — setting a size that is already in
 * effect does nothing — so a marker is written only where the size actually
 * changes, and `fontSmall` (the size currently in effect) is threaded through
 * the whole chart rather than reset per bar. Two consequences worth knowing:
 * a small chord opening the bar after a small one emits nothing, and 'l' never
 * lands immediately before an 's', which would put two contradictory
 * annotations on a single cell.
 *
 * Blank cells draw nothing, so they never force a size change. A slash does:
 * its stroke is drawn at full size, so a shrunken chord before one restores.
 */
function emitSlots(
  slots: Slot[],
  fontSmall: boolean,
): { content: string; fontSmall: boolean } {
  let result = '';

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const next = slots[i + 1];

    if (slot.token !== ' ' && slot.small !== fontSmall) {
      result += slot.small ? 's' : 'l';
      fontSmall = slot.small;
    }

    if (slot.token === 'p' || slot.token === ' ') {
      result += slot.token + ' '.repeat(slot.trailing);
      continue;
    }

    // The ',' closes the token when nothing else does: against another chord it
    // is what stops iRealPro reading the two as one, and after a shrunken chord
    // it separates the symbol from the glyph in the next cell. A trailing blank
    // cell already separates them, so no comma is needed there.
    const adjacentChord = slot.trailing === 0 && next !== undefined &&
      next.token !== 'p' && next.token !== ' ';
    const needsComma = adjacentChord || (slot.small && slot.trailing === 0);
    result += slot.token + (needsComma ? ',' : '') + ' '.repeat(slot.trailing);
  }

  return { content: result, fontSmall };
}

/**
 * Builds the cell content string for one measure (no barlines).
 *
 * `fontSmall` is the size in effect entering the measure, and comes back as the
 * size in effect leaving it (see emitSlots).
 *
 * An empty measure — barlines with no chord entries between them — is filled
 * with iRealPro's no-chord token 'n' and renders blank.
 */
function buildCellContent(
  tokens: string[],
  cells: number,
  fontSmall: boolean,
): { content: string; fontSmall: boolean } {
  if (tokens.length === 0) {
    return { content: 'n' + ' '.repeat(cells - 1), fontSmall };
  }

  const slots = allocateSlots(tokens, cells);
  assignFontSizes(slots);
  return emitSlots(slots, fontSmall);
}

// ---------------------------------------------------------------------------
// Main music string builder
// ---------------------------------------------------------------------------

const SYSTEM_CELLS = 16;

/**
 * Builds the raw (unscrambled) iRealPro music string from a chord chart array.
 * Exported for the layout tests — buildIrealProUrl is the real entry point.
 *
 * Dense measures (n > beats, needing 8+ cells) are pushed to the next system row
 * when they would overflow the current 16-cell row. The opening barline for a
 * dense measure that overflows is emitted first (closing the previous measure),
 * then bare spaces pad the remainder of the current row, and the dense content
 * starts on the next system with no additional barline prefix.
 *
 * Font size ('s'/'l') is threaded through the whole chart rather than reset per
 * measure: the markers are idempotent in iRealPro, so one is written only where
 * the size actually changes. A small chord opening the bar after a small one
 * needs no marker at all.
 */
export function buildMusicString(
  chart: ChordChartEntry[],
  timeSignature?: string | null,
  minorSymbol: 'm' | '-' | '_' = 'm',
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit' = 'explicit',
  openingBar: 'doubleBar' | 'repeatStart' = 'doubleBar',
  closingBar: 'doubleBar' | 'repeatEnd' = 'doubleBar',
): string {
  const timeSigToken = timeSignature ? `T${timeSignature}` : '';
  const beats = beatsPerBar(timeSignature);

  const measures = segmentIntoMeasures(chart, minorSymbol, altSymbol);

  // '[' is the section-open marker; time signature follows immediately inside.
  // iRealPro format: '[T44...' not 'T44[...'
  let music = `[${timeSigToken}`;
  let systemOffset = 0;   // cells used in current 16-cell row
  let fontSmall = false;  // chord size currently in effect
  // The first measure's opening barline:
  //   'doubleBar'   → no barline (content starts right after '[T44')
  //   'repeatStart' → '{' repeat-start barline before first measure's content
  // Inject opening/closing barlines from the App UI selects (not present in chordChart data).
  // First measure: '{' for repeatStart, or null for plain doubleBar.
  // Last measure: override its openBarToken with '}' for repeatEnd.
  let isFirstMeasure = true;
  if (closingBar === 'repeatEnd' && measures.length > 0) {
    // Override the last measure's opening barline with '}' (repeat-end barline).
    measures[measures.length - 1] = {
      ...measures[measures.length - 1],
      openBarToken: '}',
    };
  }

  for (let mi = 0; mi < measures.length; mi++) {
    const measure = measures[mi];
    const n = measure.slots.length;
    const isDense = !measure.isRepeat && n > beats;
    const cells = isDense ? Math.ceil(n / beats) * beats : beats;

    // Resolve this measure's effective opening barline token:
    //   - First measure: use firstBarlineToken ('{' for repeatStart, null for doubleBar)
    //   - Other measures: use measure.openBarToken as-is
    const effectiveBarline = isFirstMeasure
      ? (openingBar === 'repeatStart' ? '{' : null)
      : measure.openBarToken;
    isFirstMeasure = false;

    if (isDense && systemOffset > 0 && (systemOffset + cells > SYSTEM_CELLS)) {
      // Dense measure won't fit in the remaining cells of this row.
      // Emit the dense measure's opening barline NOW (it closes the previous measure
      // visually), then pad the rest of the row with bare spaces, then start the
      // dense measure content on the next system with NO barline prefix.
      if (effectiveBarline !== null) {
        music += effectiveBarline;
      }
      music += ' '.repeat(SYSTEM_CELLS - systemOffset);
      systemOffset = 0;
      // Dense measure content follows with no additional barline prefix.
    } else if (effectiveBarline !== null) {
      music += effectiveBarline;
    }

    if (measure.isRepeat) {
      music += measure.repeatToken;
      systemOffset = (systemOffset + beats) % SYSTEM_CELLS;
      continue;
    }

    const rendered = buildCellContent(measure.slots.map(s => s.token), cells, fontSmall);
    music += rendered.content;
    fontSmall = rendered.fontSmall;
    systemOffset = (systemOffset + cells) % SYSTEM_CELLS;
  }

  // 'Z' = final thick double bar line (chart end). ']' is a lighter section-closing
  // double barline used for intermediate section ends, not the chart's final bar.
  music += 'Z ';
  return music;
}

// ---------------------------------------------------------------------------
// URL builder
// ---------------------------------------------------------------------------

/**
 * Assembles the full irealb:// URL for a single song.
 *
 * Field layout (= separated), matching iRealPro's actual format:
 *   [0] Title
 *   [1] Composer
 *   [2] empty
 *   [3] Style
 *   [4] Key
 *   [5] empty  (transpose — iRealPro leaves this blank, not "0")
 *   [6] 1r34LbKcu7{scrambled music}
 *   [7] empty  (groove)
 *   [8] BPM
 *   [9] Repeats
 */
export function buildIrealProUrl(
  chart: ChordChartEntry[],
  options: IrealProExportOptions = {}
): string {
  const {
    title = 'Untitled',
    composer = '',
    style = 'Medium Swing',
    key = 'C',
    bpm = 120,
    repeats = 3,
    timeSignature = '44',
    minorSymbol = 'm',
    altSymbol = 'explicit',
    openingBar = 'doubleBar',
    closingBar = 'doubleBar',
  } = options;

  const musicRaw = buildMusicString(chart, timeSignature, minorSymbol, altSymbol, openingBar, closingBar);
  console.log('[iRealPro export] raw music:', musicRaw.replace(/ /g, '·'));
  const musicScrambled = scramble(musicRaw);
  console.log('[iRealPro export] scrambled:', musicScrambled);

  // iRealPro URL format: irealb://field0=field1=...=field9===PlaylistName
  // The '=' field separators must be LITERAL (not encoded).
  // Only encode individual field values that contain spaces or special chars.
  // Reference: irealb://Song%20Title=Composer==Medium%20Swing=C==1r34LbKcu7{scrambled}==120=3===Song%20Title
  const fields = [
    encodeURIComponent(title),
    encodeURIComponent(composer),
    '',                                        // [2] empty
    encodeURIComponent(style),
    encodeURIComponent(key),
    '',                                        // [5] transpose — blank
    `1r34LbKcu7${encodeURIComponent(musicScrambled)}`, // [6] sentinel + scrambled music (encoded)
    '',                                        // [7] groove — blank
    String(bpm),
    String(repeats),
  ];

  const songString = fields.join('=');
  const playlistName = encodeURIComponent(title);

  const url = `irealb://${songString}===${playlistName}`;
  console.log('[iRealPro export] full URL:', url);
  return url;
}

// ---------------------------------------------------------------------------
// Convenience: build and open in iRealPro
// ---------------------------------------------------------------------------

/**
 * Opens the chord chart directly in the iRealPro app via its URL scheme.
 * Works on iOS/macOS/Android where iRealPro is installed.
 * Returns the generated URL so you can also display or copy it.
 */
export function openInIrealPro(
  chart: ChordChartEntry[],
  options: IrealProExportOptions = {}
): string {
  const url = buildIrealProUrl(chart, options);
  // Use window.location.href instead of window.open() — window.open() re-encodes
  // characters like '|' to '%7C', breaking iRealPro's URL parser. location.href
  // treats the string as a pre-formed URI and passes it through unchanged.
  window.location.href = url;
  return url;
}
